<template>
  <div class="flex flex-col h-full mx-auto bg-ld-background overflow-hidden">
    <SafeAreaTopSpacer />
    <AppBar
      :title="appBarTitle"
      @back="onBack"
    >
      <template #actions>
        <Button
          v-if="data.package"
          size="small"
          :disabledMsg="
            _t('learn.card_require', { count: _global.minCardCountToLearn })
          "
          :disabled="data.package.cardCount < _global.minCardCountToLearn"
          :label="_t('learn.to_atlas')"
          @click="onChallenge"
        ></Button>
      </template>
    </AppBar>

    <UnpublishedPackage
      v-if="data.package != null && isPackageUpdateUnreleased(data.package)"
      :package="data.package"
      @on-update="onUpdate"
    />

    <div class="flex flex-col flex-1 relative overflow-hidden">
      <Loading
        v-if="data.packageLoading || data.cardsLoading"
        class="h-full"
      />

      <template v-else-if="data.package">
        <SlickList
          :list="data.cards"
          axis="y"
          class="flex-1 flex flex-col overflow-hidden"
          useDragHandle
          @sortEnd="onCardsSortEnd"
        >
          <CardList
            ref="cardList"
            :package="data.package"
            :chapterId="chapterId"
            :cards="data.cards"
            :is-generating="aiGenProgress.isActive"
            @chapter-click="onChapterClick"
            @create-card="onCardCreateClick"
            @ai-generate="onAiGenSelectSource"
            @animation-completed="onAnimationCompleted"
            @all-animation-completed="onAllAnimationCompleted"
            @show-title="onShowTitle"
          >
            <template #card="{ cardRes, noteIndex: cardIndex }">
              <SlickItem :index="cardIndex">
                <div
                  v-if="cardIndex > 0"
                  class="h-12px"
                ></div>

                <CardPad
                  v-if="
                    isOwner &&
                    (data.cardFocus === cardIndex || invalidCards[cardRes.id])
                  "
                  :canDelete="canCardDelete(cardRes)"
                  :canSwitchCardType="data.package.cardType == null"
                  :packageId="data.package.id"
                  :cardDraft="cardRes"
                  :cards="data.cards"
                  :class="{
                    'card-focus': data.cardFocus === cardIndex,
                  }"
                  @create-after="onCreateCard(cardIndex + 1)"
                  @create-before="onCreateCard(cardIndex)"
                  @copy="onCardCopy(chapterId, cardRes.id, cardRes)"
                  @delete="onCardDraftDelete(cardRes, cardIndex)"
                  @update="onCardDraftUpdate(cardRes, cardIndex, $event)"
                  @add-illustration="
                    onIllustrationAdd(cardRes, cardIndex, $event)
                  "
                  @remove-illustration="onIllustrationRemove(cardRes)"
                  @change-card-type="defaultCardType = $event"
                  @focus="onCardEditorFocus"
                >
                  <template
                    v-if="activeCardResponse && activeCard"
                    #footer
                  >
                    <DistractorPanel
                      v-if="activeCard.type === CardType.CLOZE"
                      :cardId="activeCardResponse.id"
                      :card="activeCard"
                      class="bg-gray-100 rounded-b-8px"
                      @focus="onCardEditorFocus"
                    ></DistractorPanel>

                    <MCQEditPanel
                      v-if="activeCard.type === CardType.MCQ"
                      :key="activeCardResponse.id"
                      :cardId="activeCardResponse.id"
                      :card="activeCard"
                      class="bg-gray-100 rounded-b-8px"
                      @focus="onCardEditorFocus"
                      @update="onMCQCardUpdate"
                    />
                  </template>
                </CardPad>

                <CardBrowserPad
                  v-else
                  :key="cardRes.id"
                  :cardResponse="cardRes"
                  :learn-count="data.cardLearnCountMap[cardRes.id]"
                  :mastery="data.cardMasteryMap[cardRes.id]"
                  :class="[
                    {
                      'card-focus': data.cardFocus === cardIndex,
                      'g-card-highlight': data.highlightCards[cardRes.id],
                    },
                  ]"
                  @click="onCardSelect(cardIndex)"
                />
              </SlickItem>
            </template>
          </CardList>
        </SlickList>
      </template>
    </div>
    <!-- AI文件生成任务进度指示器 -->
    <div
      v-if="aiGenProgress.isActive"
      class="fixed bottom-4 left-0 right-0 flex justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-md px-4 py-2 flex items-center">
        <div class="mr-2">
          <i class="pi pi-spin pi-spinner text-ld-brand-500"></i>
        </div>
        <div>
          {{
            _t('package.ai_file_gen_progress', {
              current: aiGenProgress.processedPages,
              total: aiGenProgress.totalPages,
            })
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Code } from '@/api/code'
import {
  fetchCards,
  fetchPackageById,
  type Package,
  type CardResponse,
  type ChapterItem,
  type PackageBasic,
  AiGenSourceEnum,
  createCard,
  CardCreatedType,
  deleteCard,
  CardUpdatedType,
  updateCard,
  moveCard,
  fetchCardsStats,
  CardTypeName,
  checkAiFileGenTaskProgress,
} from '@/api/package-source'
import Loading from '@/components/Loading.vue'
import AppBar from '@/mobile/components/AppBar.vue'
import { useCommonStore } from '@/stores/common-store'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { onUnmounted } from 'vue'
import { reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CardList from '@/components/package/CardList.vue'
import UnpublishedPackage from '@/components/UnpublishedPackageHeader.vue'
import { isPackageUpdateUnreleased } from '@/utils/package'
import CardBrowserPad from '@/components/package/CardBrowserPad.vue'
import CardPad from '@/components/package/CardPad/CardPad.vue'
import { SlickList, SlickItem } from 'vue-slicksort'
import {
  getCardTitleForDialog,
  isEmptyCard,
  newClozeCard,
  newKidHanziCard,
  newMCQCard,
  newWordCard,
  updateClozeCardContentDistractors,
  validateCard,
  type CardDraft,
} from '@/utils/card'
import { CardType, type Card, type ClozeCard, type MCQCard } from '@/types/core'
import dayjs from 'dayjs'
import { debounce, mapValues } from 'lodash-es'
import type { DragPosition } from '@/components/Tree/Tree.vue'
import bus, { BusEvent } from '@/bus/bus'
import AICardGenerate from '@/components/package/AICardGenerate.vue/AICardGenerate.vue'
import DistractorPanel, {
  type ClozeGroup,
} from '@/components/DistractorPanel/DistractorPanel.vue'
import MCQEditPanel from '@/components/MCQEditPanel.vue'
import { convertCardNameToCardType } from '@/shared'
import AIGenSourceTypeDialog from '@/components/package/AICardGenerate.vue/AIGenSourceTypeDialog.vue'
import AIGenCompleteDialog from '@/components/package/AICardGenerate.vue/AIGenCompleteDialog.vue'
import type { OpenDialogPromise } from '@/types/global'

const store = useCommonStore()
const route = useRoute()
const router = useRouter()
const onBack = () => {
  if (router.canGoBack) {
    router.back()
  } else {
    router.replace({
      name: 'shelf',
    })
  }
}

const packageId = Number(route.params.id)
const chapterId = computed(
  () => (route.query.chapterId as string | undefined) ?? 'root'
)

const isOwner = computed(() => data.package?.owned != null)

const defaultCardType = ref<CardType>(CardType.CLOZE)

const appBarTitle = computed(() => {
  if (data.showTitle && data.package) {
    if (chapterId.value === 'root') return data.package.name
    return data.package.chapters[chapterId.value].title
  }
  return ''
})

const activeCardResponse = computed(() => {
  if (data.cardFocus < 0) return null

  return data.cards[data.cardFocus]
})

const activeCard = computed(() => {
  if (activeCardResponse.value != null) {
    return JSON.parse(activeCardResponse.value.content) as Card
  }

  return null
})

const data = reactive({
  package: undefined as Package | undefined,
  packageLoading: true,
  cards: [] as CardDraft[],
  cardLearnCountMap: {} as Record<number, number>,
  cardMasteryMap: {} as Record<number, number>,
  cardsLoading: true,
  cardFocus: -1,
  showTitle: false,
  highlightCards: {} as Record<number, boolean>,
})

// AI文件生成任务进度状态
const aiGenProgress = reactive({
  isActive: computed<boolean>(
    // 有未完成的页码，或者还在播放动画，则显示进度条
    (): boolean =>
      aiGenProgress.totalPages != aiGenProgress.processedPages ||
      aiGenProgress.animating
  ),
  totalPages: 0,
  // TODO 这里有一个优化空间，现在processedPages是只看后台实际生成的页数，而不是客户端动画的进度，按客户端进度更加符合直觉
  // TODO 还可以根据后台生成的进度来控制客户端的打字速度
  processedPages: 0,
  lastCardId: computed<number | undefined>((): number | undefined => {
    if (data.cards.length == 0) return undefined
    else if (data.cards.length == 1 && data.cards[0].id < 0) return undefined
    else return data.cards[data.cards.length - 1].id
  }), // 记录最后一张卡片的ID，用于后续查询新卡片
  cardCount: 0, // 本次生成的卡片计数
  animating: false, // 正在播放打字机动画
  // AI生成任务信息
  taskInfo: {
    dedupPath: '',
    pages: [] as number[],
  },
})
watch(
  () => aiGenProgress.isActive,
  (value, oldValue) => {
    if (value === false && oldValue === true) {
      _openDialog(AIGenCompleteDialog, {
        rootClass: 'g-dialog',
        props: {
          cardCount: aiGenProgress.cardCount,
          cardDeficit: _global.minCardCountToLearn - data.cards.length,
          onGoToStudy: () => {
            onChallenge()
          },
          onReportError: () => {
            // 上报错误到Sentry
            _reportError({
              msg: 'AI文件生成失败',
              extra: {
                dedupPath: aiGenProgress.taskInfo.dedupPath,
                pages: aiGenProgress.taskInfo.pages,
                packageId: data.package!.id,
                chapterId: chapterId.value,
              },
            })
            _message.info(_t('common.error_report_success'))
          },
        },
        dialog: {
          showHeader: false,
        },
      })
    }
  }
)

// 如果编辑的卡片内容不合法，需要保持编辑器状态
// 这里用于存储哪些卡片未保存下来
const invalidCards = ref<Record<number, boolean>>({})

function canCardDelete(card: CardResponse) {
  // 已保存的卡片可以直接删除
  // 非最后一张草稿卡可以删除
  return card.id >= 0 || data.cards.length > 1
}

async function onCardCopy(
  chapterId: string,
  cardId: number,
  card: CardResponse
): Promise<CardResponse | undefined> {
  if (data.package == null) return
  if (cardId < 0) {
    _message.info(_t('cardview.save_tip1'))
    return
  }

  const content = JSON.parse(card.content)

  return createCard({
    packageId: data.package.id,
    chapterId,
    content,
    afterCardId: cardId,
    createdType: CardCreatedType.DUPLICATE,
  }).then(res => {
    if (res.code === 0) {
      const index = data.cards.findIndex(item => item.id === cardId)

      if (index > -1) {
        data.cards.splice(index + 1, 0, {
          renderId: getCardRenderId(),
          ...res.data,
        })
      }

      data.package!.cardCount += 1
      return res.data
    } else {
      _message.info(res.message)
    }
  })
}

let isDeleteConfirmOpen = false
async function onCardDraftDelete(cardRes: CardResponse, index: number) {
  if (isDeleteConfirmOpen) return

  isDeleteConfirmOpen = true

  const needConfirm = cardRes.id >= 0

  function onDelete() {
    if (cardRes.id > 0) {
      onCardDelete(chapterId.value, cardRes.id)
    } else {
      data.cards.splice(index, 1)
    }
  }

  if (needConfirm) {
    await _confirm({
      scene: 'warn',
      title: _t('package.delete_card_title', {
        title: getCardTitleForDialog(cardRes.content),
      }),
      content: _t('common.delete_tip'),
      primaryText: _t('common.not_yet'),
      secondaryText: _t('common.delete'),
      onSecondaryClick(resolve) {
        resolve(true)
        onDelete()
      },
    })
    isDeleteConfirmOpen = false
  } else {
    onDelete()
  }
}

async function onCardDelete(chapterId: string, cardId: number) {
  const card = data.cards.find(item => item.id === cardId)

  if (card == null || data.package == null) return

  const res = await deleteCard(data.package.id, chapterId, Number(cardId))

  if (res.code !== 0) {
    _message.info(res.message)
    return
  }

  data.package!.cardCount -= 1
  updateCardListCache()

  const index = data.cards.findIndex(item => item.id === cardId)

  if (index > -1) {
    data.cards.splice(index, 1)
    _message.info(_t('common.delete_success'))
    return true
  }
}

// 每次新建一个卡片草稿时的 id，新增时自增
let cardRenderId = 0
function getCardRenderId(): number {
  cardRenderId++

  return cardRenderId
}

function onCreateCard(index: number) {
  if (!isOwner.value) return

  let card: Card

  switch (defaultCardType.value) {
    case CardType.CLOZE:
      card = newClozeCard()
      break
    case CardType.EN_WORD:
      card = newWordCard()
      break
    case CardType.MCQ:
      card = newMCQCard()
      break
    case CardType.KID_HANZI:
      card = newKidHanziCard()
      break
    default:
      card = newClozeCard()
  }

  const renderId = getCardRenderId()
  const newCardDraft: CardDraft = {
    renderId,
    id: -renderId,
    content: JSON.stringify(card),
    contentHash: '',
    createdType: CardCreatedType.NORMAL,
    updatedType: CardUpdatedType.NORMAL,
    createdAt: dayjs().format('YYYY-DD-MM'),
    updatedAt: dayjs().format('YYYY-DD-MM'),
    authorId: '',
  }

  cardRenderId++
  data.cards.splice(index, 0, newCardDraft)
  onCardSelect(index)
  scrollToCardListBottom()
}

function onCardDraftUpdate(
  cardRes: CardResponse,
  noteIndex: number,
  newCard: Card
) {
  cardRes.content = JSON.stringify(newCard)

  if (cardRes.id < 0) {
    let afterCardId: number | undefined = undefined
    let beforeCardId: number | undefined = undefined

    for (let index = noteIndex - 1; index >= 0; index--) {
      if (data.cards[index].id > 0) {
        afterCardId = data.cards[index].id
        break
      }
    }

    for (let index = noteIndex + 1; index < data.cards.length; index++) {
      if (data.cards[index].id > 0) {
        beforeCardId = data.cards[index].id
        break
      }
    }

    saveCreatedCard(cardRes, newCard, afterCardId, beforeCardId)
  } else {
    saveUpdatedCard(cardRes.id, newCard)
  }
}

const saveUpdatedCard = debounce((cardId: number, content: Card) => {
  if (validateCard(content)) {
    invalidCards.value[cardId] = true
    return
  }

  invalidCards.value[cardId] = false
  return updateCard(cardId, content).then(res => {
    if (res.code !== 0) {
      _message.info(res.message)
      return
    }
  })
}, 500)

const saveCreatedCard = debounce(
  (
    cardRes: CardResponse,
    card: Card,
    afterCardId?: number,
    beforeCardId?: number
  ) => {
    if (data.package == null) return

    if (validateCard(card)) {
      invalidCards.value[cardRes.id] = true
      return
    }

    createCard({
      packageId: data.package.id,
      chapterId: chapterId.value,
      content: card,
      afterCardId: afterCardId,
      beforeCardId: beforeCardId,
      createdType: CardCreatedType.NORMAL,
    }).then(res => {
      if (res.code !== 0) {
        _message.info(res.message)
        return
      }

      data.package!.cardCount += 1
      cardRes.id = res.data.id
      cardRes.content = res.data.content
    })
  },
  500
)

function onIllustrationAdd(
  cardRes: CardResponse,
  cardIndex: number,
  assetId: string
) {
  if (!_global.isInsideApp) {
    showAppDownloadDialog()
    return
  }

  const cardContent = JSON.parse(cardRes.content) as Card
  const card = {
    ...cardContent,
    illustration: assetId,
  } as Card
  cardRes.content = JSON.stringify(card)

  if (cardRes.id < 0) {
    let afterCardId: number | undefined = undefined
    let beforeCardId: number | undefined = undefined

    for (let index = cardIndex - 1; index >= 0; index--) {
      if (data.cards[index].id > 0) {
        afterCardId = data.cards[index].id
        break
      }
    }

    for (let index = cardIndex + 1; index < data.cards.length; index++) {
      if (data.cards[index].id > 0) {
        beforeCardId = data.cards[index].id
        break
      }
    }

    saveCreatedCard(cardRes, card, afterCardId, beforeCardId)
    return
  }

  saveUpdatedCard(cardRes.id, {
    ...cardContent,
    illustration: assetId,
  })
}

function onIllustrationRemove(cardRes: CardResponse) {
  if (!_global.isInsideApp) {
    showAppDownloadDialog()
    return
  }

  const cardContent = JSON.parse(cardRes.content) as Card

  const newContent = {
    ...cardContent,
  }
  delete newContent.illustration
  cardRes.content = JSON.stringify(newContent)

  if (cardRes.id < 0) return
  saveUpdatedCard(cardRes.id, newContent)
}

function onCardsSortEnd({
  newIndex,
  oldIndex,
}: {
  newIndex: number
  oldIndex: number
}) {
  if (newIndex === oldIndex) return
  const focusedCardId = data.cards[data.cardFocus].id

  const sourceCard = data.cards[oldIndex]

  data.cards.splice(oldIndex, 1)
  data.cards.splice(newIndex, 0, sourceCard)

  // 如果移动的是一张草稿卡，则不需要调用接口，不影响
  if (sourceCard.id < 0) {
    return
  }

  const beforeCardId = data.cards[newIndex + 1]?.id

  if (beforeCardId != null) {
    moveCardPad(sourceCard.id, beforeCardId, 'before')
  } else {
    moveCardPad(sourceCard.id, beforeCardId, 'bottom')
  }

  data.cardFocus = data.cards.findIndex(item => item.id === focusedCardId)
}

async function moveCardPad(
  sourceCardId: number,
  targetCardId?: number,
  position?: DragPosition
) {
  if (data.package == null) return

  await moveCard({
    sourcePkgId: data.package.id,
    cardId: sourceCardId,
    beforeCardId: position === 'bottom' ? undefined : targetCardId,
    sourceChapterId: chapterId.value,
    targetChapterId: chapterId.value,
  })
}

async function fetchPackage() {
  data.packageLoading = true

  try {
    const res = await fetchPackageById(packageId)

    if (res.code === Code.PackageNotFound) {
      router.replace({
        name: '404',
      })
      return
    }

    data.package = res.data

    defaultCardType.value = convertCardNameToCardType(
      data.package.owned?.defaultCardType ??
        data.package.cardType ??
        CardTypeName.CLOZE
    )
  } finally {
    data.packageLoading = false
  }
}
const cardListCache = ref<Record<string, CardResponse[]>>({})

function updateCardListCache() {
  cardListCache.value[chapterId.value] = data.cards.filter(c => c.id > 0)
}

async function fetchCardList(chapterId: string) {
  if (chapterId == null) return []

  try {
    if (cardListCache.value[chapterId] != null) {
      data.cards = cardListCache.value[chapterId].map(card => {
        return {
          renderId: getCardRenderId(),
          ...card,
        }
      })
    } else {
      data.cardsLoading = true
      data.cards = (await fetchCards(packageId, chapterId)).data.cards.map(
        card => {
          return {
            renderId: getCardRenderId(),
            ...card,
          }
        }
      )
      cardListCache.value[chapterId] = data.cards
      data.cards.forEach(c => {
        store.setCardResponseCache(c.id, c)
      })
    }

    fetchCardsLearnCount(data.cards.map(item => item.id))
  } finally {
    data.cardsLoading = false
  }
}

function fetchCardsLearnCount(cardIds: number[]) {
  if (cardIds.length === 0) return

  fetchCardsStats(cardIds).then(res => {
    data.cardLearnCountMap = mapValues(res.cardStatsMap, v => v.learnTimes)
    data.cardMasteryMap = mapValues(res.cardStatsMap, v => v.mastery)
  })
}

// 检查并获取新生成的卡片
async function fetchNewGeneratedCards() {
  if (!data.package) return
  try {
    const response = await fetchCards(
      data.package.id,
      chapterId.value,
      aiGenProgress.lastCardId
    )

    if (response.code != Code.Ok) {
      return
    }

    if (response.data.cards.length > 0) {
      const newCards = response.data.cards.map(card => {
        return {
          renderId: getCardRenderId(),
          ...card,
        }
      })
      const newCardIds = newCards.map(item => item.id)
      aiGenProgress.cardCount += newCards.length

      if (data.cards.length == 1 && isEmptyCard(data.cards[0])) {
        data.cards.pop()
      }

      // 更新卡片列表和缓存
      data.cards.push(...newCards)
      updateCardListCache()

      // 通知 CardList 开始打字机动画
      aiGenProgress.animating = true
      cardList.value.animateNewCards(newCardIds)
    }
  } catch (error) {
    console.error('获取新生成卡片失败', error)
  }
}

// 检查AI文件生成任务进度
async function checkAiFileGenTasksProgress() {
  if (!data.package) {
    return
  }
  try {
    const progress = await checkAiFileGenTaskProgress(data.package.id)
    if (progress) {
      // 有未完成任务
      aiGenProgress.totalPages = progress.totalPages
      aiGenProgress.processedPages = progress.processedPages

      // 获取新生成的卡片
      await fetchNewGeneratedCards()

      // 任务未完成，10秒后再次检查
      if (progress.processedPages < progress.totalPages) {
        setTimeout(checkAiFileGenTasksProgress, 10000)
      } else {
        // 任务已完成
        // 最后再检查一次新卡片
        await fetchNewGeneratedCards()
      }
    } else {
      // 没有未完成任务
      // 检查是否有新卡片
      await fetchNewGeneratedCards()
      // 标记进度满
      aiGenProgress.processedPages = aiGenProgress.totalPages
    }
  } catch (error) {
    console.error('检查AI生成任务失败', error)
    // 出错时10秒后重试
    setTimeout(checkAiFileGenTasksProgress, 10000)
  }
}

function onChapterClick(chapter: ChapterItem) {
  router.push({
    query: {
      ...route.query,
      chapterId: chapter.id,
    },
  })
  fetchCardList(String(chapter.id))
}

function onRouteChange() {
  fetchCardList(chapterId.value)
}

function onUpdate(pkg: PackageBasic) {
  if (data.package) {
    Object.assign(data.package, pkg)
  } else {
    fetchPackage()
  }
}

function onAnimationCompleted(id: number) {
  highlightCard(id)
  data.package!.cardCount += 1
}

function onAllAnimationCompleted() {
  // TODO 现在的实现没有考虑切换章节的情况，切换章节会提前停止动画，可能会使 isActive 不会被清零
  aiGenProgress.animating = false
}

onMounted(() => {
  window.addEventListener('popstate', onRouteChange)
  // 移动端这里用了 bus 来监听 ai 生成结束，因为如果切换了卡片，distractorPanel 会被卸载导致
  // update 事件拿不到，所以生成的过程中就不能切换卡片了。用 bus 实现可以切换卡片
  bus.on(BusEvent.AiDistractorsGenerated, onCurrentCardContentUpdate)
})
onUnmounted(() => {
  window.removeEventListener('popstate', onRouteChange)
  bus.off(BusEvent.AiDistractorsGenerated, onCurrentCardContentUpdate)
})

// 如果这个卡包是空的，则新建一张无法删除的草稿卡片
function createDraftCards() {
  if (data.package?.cardCount === 0) {
    onCreateCard(0)
  }
}

onInit(async () => {
  data.packageLoading = true
  data.cardsLoading = true
  await fetchPackage()
  if (data.package == null) return
  await fetchCardList(chapterId.value)

  // 检查是否有未完成的AI文件生成任务
  await checkAiFileGenTasksProgress()

  watch(
    () => [data.cards.length, data.package?.cardCount],
    ([cardsLength, cardCount]) => {
      if (cardsLength === 0 && cardCount === 0) {
        createDraftCards()
      }
    },
    { immediate: true }
  )
})

function onCardSelect(index: number) {
  data.cardFocus = index
}

function highlightCard(id: number) {
  data.highlightCards[id] = true
  setTimeout(() => {
    data.highlightCards[id] = false
  }, 1000)
}

function onCurrentCardContentUpdate(
  cardId: number,
  group: ClozeGroup,
  distractors: string[]
) {
  const index = data.cards.findIndex(item => item.id === cardId)

  if (index < 0) return

  const cardRes = data.cards[index]

  const card = JSON.parse(cardRes.content) as ClozeCard
  const newContent = updateClozeCardContentDistractors(card, group, distractors)
  const newCard = {
    ...card,
    content: newContent,
  }
  data.cards.splice(index, 1, {
    ...cardRes,
    content: JSON.stringify(newCard),
  })
  onCardDraftUpdate(cardRes, index, newCard)
}

function onMCQCardUpdate(cardId: number, card: MCQCard) {
  const index = data.cards.findIndex(item => item.id === cardId)

  if (index < 0) return

  const cardRes = data.cards[index]
  data.cards.splice(index, 1, {
    ...cardRes,
    content: JSON.stringify(card),
  })
  onCardDraftUpdate(cardRes, index, card)
}

let aiGenSourceTypeDialog: OpenDialogPromise<void> | null
let aiGenerateDialog: OpenDialogPromise<void> | null
function onAiGenSelectSource() {
  if (!data.package) {
    return
  }
  if (data.package.cardType === CardTypeName.CLOZE) {
    if (aiGenSourceTypeDialog != null) {
      aiGenSourceTypeDialog.close()
      aiGenSourceTypeDialog = null
      return
    }

    aiGenSourceTypeDialog = _openDialog(AIGenSourceTypeDialog, {
      title: _t('package.ai_create_card'),
      rootClass: 'p-0 g-dialog',
      props: {
        onAiGenByDocument: () => {
          onAiGen(AiGenSourceEnum.DOCUMENT)
        },
        onAiGenByImage: () => {
          onAiGen(AiGenSourceEnum.IMAGE)
        },
        onAiGenByText: () => {
          onAiGen(AiGenSourceEnum.TEXT)
        },
      },
      dialog: {
        showHeader: false,
        contentClass: 'px-4 py-3',
      },
    })

    aiGenSourceTypeDialog.finally(() => {
      aiGenSourceTypeDialog = null
    })
  } else {
    onAiGen()
  }
}

function onAiGen(aiGenSourceType: AiGenSourceEnum = AiGenSourceEnum.TEXT) {
  if (!_global.isInsideApp) {
    showAppDownloadDialog()
    return
  }

  if (data.package == null) return

  if (aiGenSourceTypeDialog != null) {
    aiGenSourceTypeDialog.close()
    aiGenSourceTypeDialog = null
  }
  if (aiGenerateDialog != null) {
    aiGenerateDialog.close()
    aiGenerateDialog = null
  }

  aiGenerateDialog = _openDialog(AICardGenerate, {
    title: _t('package.ai_create_card'),
    rootClass: 'g-dialog',
    props: {
      packageId: data.package.id,
      chapterId: chapterId.value,
      cardType: data.package.cardType,
      aiGenSourceType: aiGenSourceType,
      onCardsCreated(cards: CardResponse[]) {
        cards.forEach(card => {
          data.cards.push({
            renderId: getCardRenderId(),
            ...card,
          })
        })

        cards.forEach(card => highlightCard(card.id))
        const firstCard = cards[0]
        if (firstCard) {
          onCardSelect(firstCard.id)
          scrollToCardListBottom()
        }
      },
      async onAiFileGen(dedupPath: string, pages: number[]) {
        aiGenProgress.totalPages = pages.length
        aiGenProgress.processedPages = 0
        aiGenProgress.cardCount = 0
        aiGenProgress.taskInfo = {
          dedupPath,
          pages,
        }
        // 检查是否有未完成的AI文件生成任务
        await checkAiFileGenTasksProgress()
      },
    },
    dialog: {
      pt: {
        content: {
          class: 'p-4 bg-ld-background',
        },
      },
    },
  })
}

function onCardCreateClick() {
  if (!_global.isInsideApp) {
    showAppDownloadDialog()
  }

  onCreateCard(data.cards.length)
}

function showAppDownloadDialog() {
  _confirm({
    scene: 'warn',
    icon: {
      type: 'svg',
      name: 'ld-avatar',
    },
    content: _t('app.download_tip2', { link: _global.pcLink }),
    primaryText: _t('app.download_tip1'),
    secondaryText: _t('common.not_yet'),
    async onPrimaryClick(resolve) {
      router.push({ name: 'app-download' })
      resolve(true)
    },
  })
}

function onCardEditorFocus() {
  if (!_global.isInsideApp) {
    // 如果不在 app 中，则需要 blur 掉聚焦的输入框，不让输入
    ;(document.activeElement as any).blur?.()

    showAppDownloadDialog()
  }
}

const cardList = ref()
function scrollToCardListBottom() {
  nextTick(() => {
    if (cardList.value) {
      cardList.value.scrollToBottom()
    }
  })
}

function onChallenge() {
  router.push({
    name: 'atlas',
    query: {
      pkgId: data.package?.id,
    },
  })
}

function onShowTitle(show: boolean) {
  data.showTitle = show
}
</script>

<style scoped>
.card-focus {
  border-color: var(--ld-brand-500);
}
</style>
